/**
 * One reading of a failed request, for the screens that do not have their own.
 *
 * The API puts its explanation in the response body, so that is what a person
 * should be shown. Axios's own `err.message` is "Request failed with status
 * code 500" — technically true and useless to act on — so it is the last
 * resort, not the first.
 *
 * The fallback is deliberately plain: a screen that cannot say what went wrong
 * should say so rather than guess at a cause.
 *
 * @param {unknown} err the rejection from axios (or anything else)
 * @param {string} [fallback] used only when nothing readable was returned
 * @returns {string} a message fit to put in a toast
 */
export const apiMessage = (err, fallback = "Something went wrong. Please try again.") => {
  const body = err?.response?.data;

  /* Some endpoints here answer with a string body rather than an object —
     usually an HTML error page, which must never reach a toast. */
  if (typeof body === "string" && body.trim() && !body.trim().startsWith("<")) {
    return body.trim();
  }

  const stated = body?.message || body?.msg || body?.error;
  if (typeof stated === "string" && stated.trim()) return stated.trim();

  /* Laravel-style validation: { errors: { email: ["taken"] } }. The first one
     is shown; the form is where the rest belong, not a toast. */
  const errors = body?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find((v) => typeof v === "string" && v.trim());
    if (first) return first.trim();
  }

  /* No response at all means the request never landed — offline, blocked,
     a preflight refused. That is worth saying plainly, because "try again"
     is genuinely the right advice and a status code is not available. */
  if (err && !err.response && err.request) {
    return "Could not reach the server. Check your connection and try again.";
  }

  return fallback;
};

export default apiMessage;
